import React, { useState, useEffect } from 'react';
import { losApi } from './losApi';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Save, Loader2, Plus, Trash2, User, Users, Briefcase, PiggyBank, CreditCard, Home, Gift, ClipboardCheck, ScrollText, FileSignature, Building2, Landmark, Coins, BadgeCheck, Stamp } from 'lucide-react';
import { toast } from '../hooks/use-toast';
import { errorMessage } from './errors';
import { AriveLockNote, isAriveManaged, toastAriveConflict } from './ariveManaged';
import PhoneInput from './shared/PhoneInput';
import MoneyInput from './shared/MoneyInput';
import NumberInput from './shared/NumberInput';
import { formatDate } from './kit/time';
import { SsnField } from './loan-file/shared/SsnField';
import { otherIncomeOptions } from './loan-file/shared/otherIncome';
import UrlaSigningPanel from './UrlaSigningPanel';

/**
 * Full URLA 1003 editor — split into collapsible sections that map to
 * the URLA 2020 form sections.
 */
export default function UrlaFullEditor({ loan, onSaved }) {
  const [urla, setUrla] = useState(loan.urla || {});
  const [saving, setSaving] = useState(false);
  const [openSection, setOpenSection] = useState('borrower');
  const [signingKey, setSigningKey] = useState(0);

  useEffect(() => setUrla(loan.urla || {}), [loan]);

  const save = async () => {
    setSaving(true);
    try {
      const updated = await losApi.updateUrla(loan.loan_file_id, urla);
      toast({ title: '1003 saved' });
      setSigningKey(k => k + 1);
      onSaved && onSaved(updated);
    } catch (e) {
      if (!toastAriveConflict(e)) toast({ title: 'Failed', description: errorMessage(e) });
    } finally { setSaving(false); }
  };

  const patch = (u) => setUrla(prev => ({ ...prev, ...u }));
  // Ticket 192 (D25): the 1003 is Arive's on a linked loan, except the SSN (Arive's feed never carries it).
  const locked = isAriveManaged(loan);

  return (
    <div className="space-y-3">
      {/* Signatures / Save bar */}
      <div className="bg-white rounded-2xl p-4 border border-slate-100 shadow-sm flex flex-wrap items-center justify-between gap-3 sticky top-16 z-20">
        <div className="flex items-center gap-2 text-sm">
          <FileSignature size={14} style={{ color: '#6BA82E' }} />
          <span className="font-semibold" style={{ color: '#070B5F' }}>URLA 1003</span>
          <span className="text-xs text-slate-500 hidden md:inline">— Uniform Residential Loan Application (2020)</span>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button onClick={save} disabled={saving} className="text-xs font-semibold px-3 py-1.5 rounded-lg text-white flex items-center gap-1.5" style={{ background: '#6BA82E' }} data-testid="urla-save-all">
            {saving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />} Save 1003
          </button>
        </div>
      </div>

      <UrlaSigningPanel loan={loan} refreshKey={signingKey} unsaved={JSON.stringify(urla) !== JSON.stringify(loan.urla || {})} onRecorded={() => onSaved && onSaved()} />

      <AriveLockNote loan={loan} testId="urla-arive-note">The 1003 is edited in Arive. The SSN stays editable here.</AriveLockNote>

      <Section id="borrower" title="1a · Borrower Information" icon={User} openId={openSection} onOpen={setOpenSection}>
        <BorrowerBlock locked={locked} loanId={loan.loan_file_id} who="borrower" person={urla.borrower || {}} onChange={(b) => patch({ borrower: b })} />
      </Section>

      <Section id="coborrower" title="1a · Co-Borrower (optional)" icon={Users} openId={openSection} onOpen={setOpenSection}>
        {urla.coborrower ? (
          <div className="space-y-3">
            <BorrowerBlock locked={locked} loanId={loan.loan_file_id} who="coborrower" person={urla.coborrower} onChange={(b) => patch({ coborrower: b })} />
            <button onClick={() => patch({ coborrower: null })} disabled={locked} className="text-xs text-red-600 hover:underline disabled:opacity-40">Remove co-borrower</button>
          </div>
        ) : (
          <div>
            {/* The server keeps a stored co-borrower's SSN and e-consent until a save drops them,
                so a replacement can only be added after the removal is saved. */}
            <button onClick={() => patch({ coborrower: {} })} disabled={!!loan.urla?.coborrower || locked} className="text-xs font-semibold px-3 py-2 rounded-lg text-white inline-flex items-center gap-1.5 disabled:opacity-40" style={{ background: '#070B5F' }} data-testid="add-coborrower">
              <Plus size={12} /> Add Co-Borrower
            </button>
            {loan.urla?.coborrower && (
              <div className="text-[11px] text-slate-400 mt-2" data-testid="add-coborrower-note">Save to finish removing the current co-borrower, then add the new one.</div>
            )}
          </div>
        )}
      </Section>

      <Section id="employment" title="1b/c/d · Employment & Income" icon={Briefcase} openId={openSection} onOpen={setOpenSection}>
        <fieldset disabled={locked} style={{ display: 'contents' }}>
        <EmploymentList
          label="Current Employment"
          list={urla.current_employment || []}
          onChange={(l) => patch({ current_employment: l })}
          allowSelfEmployed
        />
        <hr className="my-4 border-slate-100" />
        <EmploymentList
          label="Previous Employment (if < 2 yrs at current)"
          list={urla.previous_employment || []}
          onChange={(l) => patch({ previous_employment: l })}
          isPrevious
        />
        <hr className="my-4 border-slate-100" />
        <OtherIncomeList list={urla.other_income || []} onChange={(l) => patch({ other_income: l })} />
        </fieldset>
      </Section>

      <Section id="assets" title="2a · Assets" icon={PiggyBank} openId={openSection} onOpen={setOpenSection}>
        <fieldset disabled={locked} style={{ display: 'contents' }}>
        <AssetsEditor list={urla.assets || []} onChange={(l) => patch({ assets: l })} />
        </fieldset>
      </Section>

      <Section id="liabilities" title="2c · Liabilities" icon={CreditCard} openId={openSection} onOpen={setOpenSection}>
        <fieldset disabled={locked} style={{ display: 'contents' }}>
        <LiabilitiesEditor list={urla.liabilities || []} onChange={(l) => patch({ liabilities: l })} />
        </fieldset>
      </Section>

      <Section id="reo" title="3 · Real Estate Owned" icon={Home} openId={openSection} onOpen={setOpenSection}>
        <fieldset disabled={locked} style={{ display: 'contents' }}>
        <REOEditor list={urla.reo_properties || []} onChange={(l) => patch({ reo_properties: l })} />
        </fieldset>
      </Section>

      <Section id="property" title="4a · Loan and Property Information" icon={Building2} openId={openSection} onOpen={setOpenSection}>
        <fieldset disabled={locked} style={{ display: 'contents' }}>
        <PropertyDetailsEditor details={urla.property_details || {}} onChange={(d) => patch({ property_details: d })} />
        </fieldset>
      </Section>

      <Section id="other-mortgages" title="4b · Other New Mortgage Loans" icon={Landmark} openId={openSection} onOpen={setOpenSection}>
        <fieldset disabled={locked} style={{ display: 'contents' }}>
        <OtherMortgagesEditor list={urla.other_new_mortgages || []} onChange={(l) => patch({ other_new_mortgages: l })} />
        </fieldset>
      </Section>

      <Section id="rental" title="4c · Rental Income on the Property" icon={Coins} openId={openSection} onOpen={setOpenSection}>
        <fieldset disabled={locked} style={{ display: 'contents' }}>
        <RentalIncomeEditor rental={urla.expected_rental_income || {}} onChange={(r) => patch({ expected_rental_income: r })} />
        </fieldset>
      </Section>

      <Section id="gifts" title="4d · Gifts or Grants" icon={Gift} openId={openSection} onOpen={setOpenSection}>
        <fieldset disabled={locked} style={{ display: 'contents' }}>
        <GiftsEditor list={urla.gifts_or_grants || []} onChange={(l) => patch({ gifts_or_grants: l })} />
        </fieldset>
      </Section>

      <Section id="declarations" title="5 · Declarations" icon={ClipboardCheck} openId={openSection} onOpen={setOpenSection}>
        <fieldset disabled={locked} style={{ display: 'contents' }}>
        <DeclarationsEditor decl={urla.declarations || {}} onChange={(d) => patch({ declarations: d })} />
        </fieldset>
      </Section>

      <Section id="acknowledgements" title="6 · Acknowledgments and Agreements" icon={BadgeCheck} openId={openSection} onOpen={setOpenSection}>
        <AcknowledgementsNote urla={urla} />
      </Section>

      <Section id="demographics" title="7 · Demographic Information (HMDA)" icon={ScrollText} openId={openSection} onOpen={setOpenSection}>
        <fieldset disabled={locked} style={{ display: 'contents' }}>
        <DemographicsEditor demo={urla.demographics_borrower || {}} onChange={(d) => patch({ demographics_borrower: d })} label="Borrower" />
        {urla.coborrower && (
          <div className="mt-4 pt-4 border-t border-slate-100">
            <DemographicsEditor demo={urla.demographics_coborrower || {}} onChange={(d) => patch({ demographics_coborrower: d })} label="Co-Borrower" />
          </div>
        )}
        </fieldset>
      </Section>

      <Section id="originator" title="9 · Loan Originator Information" icon={Stamp} openId={openSection} onOpen={setOpenSection}>
        <fieldset disabled={locked} style={{ display: 'contents' }}>
        <OriginatorEditor loanId={loan.loan_file_id} originator={urla.originator || {}} onChange={(o) => patch({ originator: o })} />
        </fieldset>
      </Section>
    </div>
  );
}

// =========================================================================
// Reusable Section wrapper
// =========================================================================
function Section({ id, title, icon: Icon, children, openId, onOpen }) {
  const open = openId === id;
  return (
    <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
      <button onClick={() => onOpen(open ? null : id)} className="w-full flex items-center gap-3 px-5 py-3.5 hover:bg-slate-50 transition text-left" data-testid={`urla-section-${id}`}>
        <Icon size={16} style={{ color: '#6BA82E' }} />
        <div className="flex-1 font-serif font-semibold text-sm" style={{ color: '#070B5F' }}>{title}</div>
        <span className="text-xs text-slate-400">{open ? '−' : '+'}</span>
      </button>
      {open && <div className="p-5 border-t border-slate-100">{children}</div>}
    </div>
  );
}

// =========================================================================
// Section 1a - Borrower / Co-borrower
// =========================================================================
const HOUSING = [['own', 'Own'], ['rent', 'Rent'], ['no_expense', 'No primary housing expense']];
const LANGUAGES = [
  ['english', 'English'], ['chinese', 'Chinese'], ['korean', 'Korean'], ['spanish', 'Spanish'],
  ['tagalog', 'Tagalog'], ['vietnamese', 'Vietnamese'], ['other', 'Other'], ['do_not_wish_to_respond', 'I do not wish to respond'],
];
const HOUSING_EXPENSE = [
  ['first_mortgage_pi', 'First Mortgage (P&I)'],
  ['other_financing_pi', 'Other Financing (P&I)'],
  ['hazard_insurance', 'Hazard Insurance'],
  ['real_estate_taxes', 'Real Estate Taxes'],
  ['mortgage_insurance', 'Mortgage Insurance'],
  ['hoa_dues', 'HOA Dues'],
  ['other', 'Other'],
];
const toNum = (v) => (v === '' || v === null || v === undefined ? null : Number(v));

function AddressFields({ addr, onChange, prefix }) {
  const a = addr || {};
  const set = (k, v) => onChange({ ...a, [k]: v });
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid={prefix}>
      <div className="md:col-span-2"><F label="Street" value={a.street} onChange={(v) => set('street', v)} /></div>
      <F label="Unit" value={a.unit} onChange={(v) => set('unit', v)} />
      <F label="City" value={a.city} onChange={(v) => set('city', v)} />
      <F label="State" value={a.state} onChange={(v) => set('state', v.toUpperCase())} />
      <F label="ZIP" value={a.zip} onChange={(v) => set('zip', v)} />
    </div>
  );
}

function BorrowerBlock({ loanId, who, person, onChange, locked }) {
  const u = (k, v) => onChange({ ...person, [k]: v });
  const expense = person.present_housing_expense || {};
  const uExpense = (k, v) => u('present_housing_expense', { ...expense, [k]: toNum(v) });
  const expenseTotal = HOUSING_EXPENSE.reduce((s, [k]) => s + (Number(expense[k]) || 0), 0);
  const years = person.years_at_address;
  // URLA asks for the former address when the borrower has been at the current one under two years.
  const showFormer = (years !== null && years !== undefined && years !== '' && Number(years) < 2) || !!person.former_address?.street;
  const ages = Array.isArray(person.dependents_ages) ? person.dependents_ages.join(', ') : (person.dependents_ages || '');
  return (
    <div className="space-y-4">
      {/* The SSN cell sits between two disabled fieldsets: a nested fieldset cannot re-enable it. */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <fieldset disabled={locked} style={{ display: 'contents' }}>
        <F label="First Name" value={person.first_name} onChange={(v) => u('first_name', v)} />
        <F label="Middle" value={person.middle_name} onChange={(v) => u('middle_name', v)} />
        <F label="Last Name" value={person.last_name} onChange={(v) => u('last_name', v)} />
        <F label="Suffix" value={person.suffix} onChange={(v) => u('suffix', v)} placeholder="Jr, Sr, III" />
        <div className="col-span-2 md:col-span-4">
          <F label="Alternate Names (names known by or credit received under)" value={person.alternate_names} onChange={(v) => u('alternate_names', v)} testId={`urla-${who}-alternate-names`} />
        </div>
        </fieldset>
        <SsnField loanId={loanId} who={who} value={person.ssn} onChange={(v) => u('ssn', v)} testId={`urla-${who}-ssn`} />
        <fieldset disabled={locked} style={{ display: 'contents' }}>
        <F label="DOB" type="date" value={person.dob} onChange={(v) => u('dob', v)} />
        <SF label="Citizenship" value={person.citizenship} onChange={(v) => u('citizenship', v)}
           options={[['us_citizen','US Citizen'],['permanent_resident','Permanent Resident'],['non_permanent_resident','Non-Permanent Resident']]} />
        <SF label="Marital Status" value={person.marital_status} onChange={(v) => u('marital_status', v)}
           options={[['married','Married'],['unmarried','Unmarried'],['separated','Separated']]} />
        <F label="Email" value={person.email} onChange={(v) => u('email', v)} />
        <F label="Cell Phone" kind="phone" value={person.cell_phone} onChange={(v) => u('cell_phone', v)} />
        <F label="Home Phone" kind="phone" value={person.home_phone} onChange={(v) => u('home_phone', v)} />
        <F label="Work Phone" kind="phone" value={person.work_phone} onChange={(v) => u('work_phone', v)} />
        <F label="# Dependents" kind="number" value={person.dependents_count} onChange={(v) => u('dependents_count', Number(v))} />
        <F label="Dependents' Ages" value={ages} onChange={(v) => u('dependents_ages', v)} placeholder="e.g. 4, 9, 12" testId={`urla-${who}-dependents-ages`} />
        <SF label="Language Preference" value={person.language_preference} onChange={(v) => u('language_preference', v)} options={LANGUAGES} testId={`urla-${who}-language`} />
        {person.language_preference === 'other' && (
          <F label="Other Language" value={person.language_preference_other} onChange={(v) => u('language_preference_other', v)} testId={`urla-${who}-language-other`} />
        )}
        </fieldset>
      </div>

      <fieldset disabled={locked} className="space-y-4 border-0 p-0 m-0 min-w-0">
      <div>
        <div className="text-xs uppercase tracking-widest text-slate-500 mb-2">Current Address</div>
        <AddressFields addr={person.current_address} onChange={(a) => u('current_address', a)} prefix={`urla-${who}-current-address`} />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3">
          <F label="Yrs at Current Address" kind="number" value={person.years_at_address} onChange={(v) => u('years_at_address', toNum(v))} testId={`urla-${who}-years-at-address`} />
          <SF label="Housing" value={person.housing_type} onChange={(v) => u('housing_type', v)} options={HOUSING} testId={`urla-${who}-housing`} />
          {person.housing_type !== 'own' && person.housing_type !== 'no_expense' && (
            <F label="Monthly Rent" kind="money" value={person.monthly_rent} onChange={(v) => u('monthly_rent', toNum(v))} />
          )}
        </div>
      </div>

      {person.housing_type === 'own' && (
        <div data-testid={`urla-${who}-housing-expense`}>
          <div className="text-xs uppercase tracking-widest text-slate-500 mb-2">Present Housing Expense (monthly)</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {HOUSING_EXPENSE.map(([k, l]) => (
              <F key={k} label={l} kind="money" value={expense[k]} onChange={(v) => uExpense(k, v)} />
            ))}
            <div>
              <div className="text-xs text-slate-500">Total</div>
              <div className="mt-1 h-9 flex items-center text-sm font-semibold" style={{ color: '#070B5F' }} data-testid={`urla-${who}-housing-expense-total`}>
                ${expenseTotal.toLocaleString()}
              </div>
            </div>
          </div>
        </div>
      )}

      {showFormer && (
        <div>
          <div className="text-xs uppercase tracking-widest text-slate-500 mb-2">Former Address (at current address under 2 years)</div>
          <AddressFields addr={person.former_address} onChange={(a) => u('former_address', a)} prefix={`urla-${who}-former-address`} />
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3">
            <F label="Yrs at Former Address" kind="number" value={person.years_at_former_address} onChange={(v) => u('years_at_former_address', toNum(v))} />
            <SF label="Housing" value={person.former_housing_type} onChange={(v) => u('former_housing_type', v)} options={HOUSING} />
            {person.former_housing_type === 'rent' && (
              <F label="Monthly Rent" kind="money" value={person.former_monthly_rent} onChange={(v) => u('former_monthly_rent', toNum(v))} />
            )}
          </div>
        </div>
      )}

      <div>
        <label className="flex items-center gap-2 text-xs">
          <input type="checkbox" checked={!!person.mailing_same} onChange={(e) => u('mailing_same', e.target.checked)} data-testid={`urla-${who}-mailing-same`} />
          Mailing address same as current
        </label>
        {!person.mailing_same && (
          <div className="mt-2">
            <div className="text-xs uppercase tracking-widest text-slate-500 mb-2">Mailing Address</div>
            <AddressFields addr={person.mailing_address} onChange={(a) => u('mailing_address', a)} prefix={`urla-${who}-mailing-address`} />
          </div>
        )}
      </div>
      </fieldset>
    </div>
  );
}

// =========================================================================
// Employment
// =========================================================================
const INCOME_SPLIT = [
  ['base_monthly', 'Base'],
  ['overtime_monthly', 'Overtime'],
  ['bonus_monthly', 'Bonus'],
  ['commission_monthly', 'Commission'],
  ['military_entitlements_monthly', 'Military Entitlements'],
  ['other_monthly', 'Other'],
];
const hasSplit = (e) => INCOME_SPLIT.some(([k]) => e[k] !== null && e[k] !== undefined && e[k] !== '');
// Same rule as the server: a salaried row with any split part totals the parts.
const splitTotal = (e) => Math.round(INCOME_SPLIT.reduce((s, [k]) => s + (Number(e[k]) || 0), 0) * 100) / 100;

function EmploymentList({ label, list, onChange, allowSelfEmployed, isPrevious }) {
  const add = () => onChange([...list, { is_current: !isPrevious, is_self_employed: false, monthly_gross: 0 }]);
  const upd = (i, patch) => onChange(list.map((x, idx) => {
    if (idx !== i) return x;
    const next = { ...x, ...patch };
    if (next.is_self_employed) return next;
    // Clearing the last split part clears the total it made, not leaves its last value.
    const touchedSplit = INCOME_SPLIT.some(([k]) => k in patch);
    return hasSplit(next) || touchedSplit ? { ...next, monthly_gross: splitTotal(next) } : next;
  }));
  const setSelfEmployed = (i, on) => upd(i, on
    ? { is_self_employed: true, ...Object.fromEntries(INCOME_SPLIT.map(([k]) => [k, null])) }
    : { is_self_employed: false, ownership_pct: null });
  const rem = (i) => onChange(list.filter((_, idx) => idx !== i));
  const kind = isPrevious ? 'prev' : 'current';
  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <div className="text-xs uppercase tracking-widest text-slate-500">{label}</div>
        <button onClick={add} className="text-xs font-semibold px-2.5 py-1 rounded-lg border border-slate-200 inline-flex items-center gap-1" data-testid={`add-${kind}-employment`}>
          <Plus size={11} /> Add
        </button>
      </div>
      <div className="space-y-2">
        {list.map((e, i) => {
          const addr = e.employer_address || {};
          const uAddr = (k, v) => upd(i, { employer_address: { ...addr, [k]: v } });
          return (
            <div key={i} className="rounded-lg border border-slate-100 p-3 space-y-3 relative" data-testid={`${kind}-employment-${i}`}>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 pr-6">
                <F label="Employer" value={e.employer_name} onChange={(v) => upd(i, { employer_name: v })} />
                <F label="Position/Title" value={e.position} onChange={(v) => upd(i, { position: v })} />
                <F label="Employer Phone" kind="phone" value={e.employer_phone} onChange={(v) => upd(i, { employer_phone: v })} />
                <F label="Yrs in Line of Work" kind="number" value={e.years_in_line_of_work} onChange={(v) => upd(i, { years_in_line_of_work: toNum(v) })} />
                <F label="Start Date" type="date" value={e.start_date} onChange={(v) => upd(i, { start_date: v })} />
                {isPrevious && <F label="End Date" type="date" value={e.end_date} onChange={(v) => upd(i, { end_date: v })} />}
                {allowSelfEmployed && (
                  <label className="flex items-center gap-2 text-xs mt-5">
                    <input type="checkbox" checked={!!e.is_self_employed} onChange={(x) => setSelfEmployed(i, x.target.checked)} />
                    Self-employed
                  </label>
                )}
                {e.is_self_employed && (
                  <F label="Ownership %" kind="number" value={e.ownership_pct} onChange={(v) => upd(i, { ownership_pct: toNum(v) })} />
                )}
              </div>
              <div className="grid grid-cols-2 md:grid-cols-6 gap-2">
                <div className="md:col-span-2"><F label="Employer Street" value={addr.street} onChange={(v) => uAddr('street', v)} /></div>
                <F label="Unit" value={addr.unit} onChange={(v) => uAddr('unit', v)} />
                <F label="City" value={addr.city} onChange={(v) => uAddr('city', v)} />
                <F label="State" value={addr.state} onChange={(v) => uAddr('state', v.toUpperCase())} />
                <F label="ZIP" value={addr.zip} onChange={(v) => uAddr('zip', v)} />
              </div>
              {e.is_self_employed ? (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                  <F label="Monthly Income" kind="money" value={e.monthly_gross} onChange={(v) => upd(i, { monthly_gross: toNum(v) })} />
                </div>
              ) : (
                <div>
                  <div className="text-[11px] text-slate-500 mb-1">Gross monthly income</div>
                  <div className="grid grid-cols-2 md:grid-cols-7 gap-2 items-end">
                    {INCOME_SPLIT.map(([k, l]) => (
                      <F key={k} label={l} kind="money" value={e[k]} onChange={(v) => upd(i, { [k]: toNum(v) })} />
                    ))}
                    <div data-testid={`${kind}-employment-${i}-total`}>
                      <div className="text-xs text-slate-500">Total</div>
                      <div className="mt-1 h-9 flex items-center text-sm font-semibold" style={{ color: '#070B5F' }}>
                        ${Number(e.monthly_gross || 0).toLocaleString()}
                      </div>
                    </div>
                  </div>
                  {!hasSplit(e) && Number(e.monthly_gross || 0) !== 0 && (
                    <div className="text-[11px] text-slate-400 mt-1">Total entered without the split. Enter the parts to replace it.</div>
                  )}
                </div>
              )}
              <label className="flex items-center gap-2 text-xs">
                <input type="checkbox" checked={!!e.employed_by_party_to_transaction} onChange={(x) => upd(i, { employed_by_party_to_transaction: x.target.checked })} />
                Employed by a family member, property seller, real estate agent, or other party to the transaction
              </label>
              <button onClick={() => rem(i)} className="absolute top-2 right-2 text-slate-400 hover:text-red-500" aria-label="Remove employment"><Trash2 size={13} /></button>
            </div>
          );
        })}
        {list.length === 0 && <div className="text-xs text-slate-400 italic">None added.</div>}
      </div>
    </div>
  );
}

function OtherIncomeList({ list, onChange }) {
  const add = () => onChange([...list, { is_current: true, other_income_type: null, monthly_gross: 0 }]);
  const upd = (i, patch) => onChange(list.map((x, idx) => idx === i ? { ...x, ...patch } : x));
  const rem = (i) => onChange(list.filter((_, idx) => idx !== i));
  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <div className="text-xs uppercase tracking-widest text-slate-500">Other Income Sources</div>
        <button onClick={add} className="text-xs font-semibold px-2.5 py-1 rounded-lg border border-slate-200 inline-flex items-center gap-1" data-testid="add-other-employment">
          <Plus size={11} /> Add
        </button>
      </div>
      <div className="space-y-2">
        {list.map((e, i) => (
          <div key={i} className="rounded-lg border border-slate-100 p-3 grid grid-cols-2 md:grid-cols-4 gap-2 relative" data-testid={`other-income-${i}`}>
            <SF label="Income Source" value={e.other_income_type} onChange={(v) => upd(i, { other_income_type: v })} options={otherIncomeOptions(e.other_income_type)} testId={`other-income-${i}-type`} />
            {e.other_income_type === 'other' && (
              <F label="Describe" value={e.other_income_description} onChange={(v) => upd(i, { other_income_description: v })} />
            )}
            <F label="Monthly Income" kind="money" value={e.monthly_gross} onChange={(v) => upd(i, { monthly_gross: Number(v) })} />
            <button onClick={() => rem(i)} className="absolute top-2 right-2 text-slate-400 hover:text-red-500" aria-label="Remove income"><Trash2 size={13} /></button>
          </div>
        ))}
        {list.length === 0 && <div className="text-xs text-slate-400 italic">None added.</div>}
      </div>
    </div>
  );
}

// =========================================================================
// Assets
// =========================================================================
function AssetsEditor({ list, onChange }) {
  const add = () => onChange([...list, { account_type: 'checking', balance: 0, owned_by: 'borrower' }]);
  const upd = (i, patch) => onChange(list.map((x, idx) => idx === i ? { ...x, ...patch } : x));
  const rem = (i) => onChange(list.filter((_, idx) => idx !== i));
  const total = list.reduce((s, a) => s + Number(a.balance || 0), 0);
  return (
    <div>
      <div className="flex justify-between items-center mb-3">
        <div className="text-xs text-slate-500">Total: <b className="font-serif" style={{ color: '#6BA82E' }}>${total.toLocaleString()}</b></div>
        <button onClick={add} className="text-xs font-semibold px-2.5 py-1 rounded-lg border border-slate-200 inline-flex items-center gap-1" data-testid="add-asset"><Plus size={11} /> Add Asset</button>
      </div>
      <div className="space-y-2">
        {list.map((a, i) => (
          <div key={i} className="rounded-lg border border-slate-100 p-3 grid grid-cols-2 md:grid-cols-5 gap-2 relative">
            <SF label="Type" value={a.account_type} onChange={(v) => upd(i, { account_type: v })}
              options={[['checking','Checking'],['savings','Savings'],['money_market','Money Market'],['cd','CD'],['retirement','Retirement'],['stocks','Stocks'],['bonds','Bonds'],['mutual_funds','Mutual Funds'],['gift','Gift'],['other','Other']]} />
            <F label="Institution" value={a.institution} onChange={(v) => upd(i, { institution: v })} />
            <F label="Acct # (last 4)" value={a.account_number_last4} onChange={(v) => upd(i, { account_number_last4: v })} />
            <F label="Balance" kind="money" value={a.balance} onChange={(v) => upd(i, { balance: Number(v) })} />
            <SF label="Owned By" value={a.owned_by} onChange={(v) => upd(i, { owned_by: v })}
              options={[['borrower','Borrower'],['coborrower','Co-Borrower'],['joint','Joint']]} />
            <button onClick={() => rem(i)} className="absolute top-2 right-2 text-slate-400 hover:text-red-500"><Trash2 size={13} /></button>
          </div>
        ))}
        {list.length === 0 && <div className="text-xs text-slate-400 italic">No assets added.</div>}
      </div>
    </div>
  );
}

// =========================================================================
// Liabilities
// =========================================================================
function LiabilitiesEditor({ list, onChange }) {
  const add = () => onChange([...list, { liability_type: 'revolving', monthly_payment: 0, unpaid_balance: 0, to_be_paid_off: false, owned_by: 'borrower' }]);
  const upd = (i, patch) => onChange(list.map((x, idx) => idx === i ? { ...x, ...patch } : x));
  const rem = (i) => onChange(list.filter((_, idx) => idx !== i));
  return (
    <div>
      <div className="flex justify-between items-center mb-3">
        <div className="text-xs text-slate-500">{list.length} liabilities</div>
        <button onClick={add} className="text-xs font-semibold px-2.5 py-1 rounded-lg border border-slate-200 inline-flex items-center gap-1" data-testid="add-liability"><Plus size={11} /> Add Liability</button>
      </div>
      <div className="space-y-2">
        {list.map((l, i) => (
          <div key={i} className="rounded-lg border border-slate-100 p-3 grid grid-cols-2 md:grid-cols-6 gap-2 relative">
            <SF label="Type" value={l.liability_type} onChange={(v) => upd(i, { liability_type: v })}
              options={[['revolving','Revolving'],['installment','Installment'],['mortgage','Mortgage'],['heloc','HELOC'],['lease','Lease'],['child_support','Child Support'],['alimony','Alimony'],['job_related_expense','Job-related'],['other','Other']]} />
            <F label="Creditor" value={l.creditor} onChange={(v) => upd(i, { creditor: v })} />
            <F label="Acct # (last 4)" value={l.account_number_last4} onChange={(v) => upd(i, { account_number_last4: v })} />
            <F label="Monthly Pmt" kind="money" value={l.monthly_payment} onChange={(v) => upd(i, { monthly_payment: Number(v) })} />
            <F label="Unpaid Bal" kind="money" value={l.unpaid_balance} onChange={(v) => upd(i, { unpaid_balance: Number(v) })} />
            <F label="Months Left" kind="number" value={l.months_left} onChange={(v) => upd(i, { months_left: Number(v) })} />
            <label className="flex items-center gap-2 text-xs md:col-span-2 mt-2">
              <input type="checkbox" checked={!!l.to_be_paid_off} onChange={(e) => upd(i, { to_be_paid_off: e.target.checked })} />
              To be paid off at/before closing
            </label>
            <button onClick={() => rem(i)} className="absolute top-2 right-2 text-slate-400 hover:text-red-500"><Trash2 size={13} /></button>
          </div>
        ))}
        {list.length === 0 && <div className="text-xs text-slate-400 italic">No liabilities added.</div>}
      </div>
    </div>
  );
}

// =========================================================================
// REO
// =========================================================================
function REOEditor({ list, onChange }) {
  const add = () => onChange([...list, { address: {}, property_value: 0, mortgage_balance: 0, monthly_mortgage: 0, monthly_rental_income: 0 }]);
  const upd = (i, patch) => onChange(list.map((x, idx) => idx === i ? { ...x, ...patch } : x));
  const rem = (i) => onChange(list.filter((_, idx) => idx !== i));
  return (
    <div>
      <div className="flex justify-between items-center mb-3">
        <div className="text-xs text-slate-500">{list.length} propert{list.length === 1 ? 'y' : 'ies'}</div>
        <button onClick={add} className="text-xs font-semibold px-2.5 py-1 rounded-lg border border-slate-200 inline-flex items-center gap-1" data-testid="add-reo"><Plus size={11} /> Add REO</button>
      </div>
      <div className="space-y-3">
        {list.map((r, i) => (
          <div key={i} className="rounded-lg border border-slate-100 p-3 relative">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-3">
              <div className="md:col-span-2"><F label="Street" value={r.address?.street} onChange={(v) => upd(i, { address: { ...r.address, street: v } })} /></div>
              <F label="City" value={r.address?.city} onChange={(v) => upd(i, { address: { ...r.address, city: v } })} />
              <div className="grid grid-cols-2 gap-2">
                <F label="State" value={r.address?.state} onChange={(v) => upd(i, { address: { ...r.address, state: v.toUpperCase() } })} />
                <F label="ZIP" value={r.address?.zip} onChange={(v) => upd(i, { address: { ...r.address, zip: v } })} />
              </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
              <F label="Property Value" kind="money" value={r.property_value} onChange={(v) => upd(i, { property_value: Number(v) })} />
              <F label="Mortgage Balance" kind="money" value={r.mortgage_balance} onChange={(v) => upd(i, { mortgage_balance: Number(v) })} />
              <F label="Monthly Mortgage" kind="money" value={r.monthly_mortgage} onChange={(v) => upd(i, { monthly_mortgage: Number(v) })} />
              <F label="Taxes/Ins/HOA" kind="money" value={r.monthly_taxes_ins_hoa} onChange={(v) => upd(i, { monthly_taxes_ins_hoa: Number(v) })} />
              <F label="Rental Income" kind="money" value={r.monthly_rental_income} onChange={(v) => upd(i, { monthly_rental_income: Number(v) })} />
            </div>
            <div className="mt-2">
              <SF label="Intended Status" value={r.intended_status} onChange={(v) => upd(i, { intended_status: v })}
                options={[['sold','Sold'],['pending_sale','Pending Sale'],['retained_primary','Keep as Primary'],['retained_second','Keep as Second Home'],['retained_investment','Keep as Investment']]} />
            </div>
            <button onClick={() => rem(i)} className="absolute top-2 right-2 text-slate-400 hover:text-red-500"><Trash2 size={13} /></button>
          </div>
        ))}
        {list.length === 0 && <div className="text-xs text-slate-400 italic">No properties owned.</div>}
      </div>
    </div>
  );
}

// =========================================================================
// Gifts / Grants
// =========================================================================
function GiftsEditor({ list, onChange }) {
  const add = () => onChange([...list, { source: '', amount: 0, is_deposited: false }]);
  const upd = (i, patch) => onChange(list.map((x, idx) => idx === i ? { ...x, ...patch } : x));
  const rem = (i) => onChange(list.filter((_, idx) => idx !== i));
  return (
    <div>
      <div className="flex justify-between items-center mb-3">
        <div className="text-xs text-slate-500">{list.length} gift(s)</div>
        <button onClick={add} className="text-xs font-semibold px-2.5 py-1 rounded-lg border border-slate-200 inline-flex items-center gap-1" data-testid="add-gift"><Plus size={11} /> Add Gift/Grant</button>
      </div>
      <div className="space-y-2">
        {list.map((g, i) => (
          <div key={i} className="rounded-lg border border-slate-100 p-3 grid grid-cols-2 md:grid-cols-4 gap-2 relative">
            <F label="Source" value={g.source} onChange={(v) => upd(i, { source: v })} placeholder="e.g., Parent, Employer, Non-profit" />
            <F label="Asset Type" value={g.asset_type} onChange={(v) => upd(i, { asset_type: v })} placeholder="Cash, Grant, etc." />
            <F label="Amount" kind="money" value={g.amount} onChange={(v) => upd(i, { amount: Number(v) })} />
            <label className="flex items-center gap-2 text-xs mt-5">
              <input type="checkbox" checked={!!g.is_deposited} onChange={(e) => upd(i, { is_deposited: e.target.checked })} />
              Already deposited
            </label>
            <button onClick={() => rem(i)} className="absolute top-2 right-2 text-slate-400 hover:text-red-500"><Trash2 size={13} /></button>
          </div>
        ))}
        {list.length === 0 && <div className="text-xs text-slate-400 italic">No gifts or grants.</div>}
      </div>
    </div>
  );
}

// =========================================================================
// 4a / 4b / 4c - Loan and property details
// =========================================================================
function PropertyDetailsEditor({ details, onChange }) {
  const u = (k, v) => onChange({ ...details, [k]: v });
  const yn = (k, text) => (
    <label className="flex items-center gap-2 text-xs">
      <input type="checkbox" checked={!!details[k]} onChange={(e) => u(k, e.target.checked)} data-testid={`property-${k}`} />
      {text}
    </label>
  );
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <SF label="Number of units" value={details.number_of_units ? String(details.number_of_units) : ''} onChange={(v) => u('number_of_units', Number(v))} testId="property-units"
          options={[['1', '1'], ['2', '2'], ['3', '3'], ['4', '4']]} />
        <SF label="Estate held" value={details.estate_held} onChange={(v) => u('estate_held', v)} testId="property-estate"
          options={[['fee_simple', 'Fee simple'], ['leasehold', 'Leasehold']]} />
        {details.estate_held === 'leasehold' && (
          <F label="Leasehold expires" type="date" value={details.leasehold_expiration} onChange={(v) => u('leasehold_expiration', v)} testId="property-leasehold-expiration" />
        )}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <F label="Title will be held in what name(s)" value={details.title_names} onChange={(v) => u('title_names', v)} testId="property-title-names" />
        <F label="Manner in which title will be held" value={details.title_manner} onChange={(v) => u('title_manner', v)} testId="property-title-manner" />
      </div>
      <div className="flex flex-wrap gap-x-6 gap-y-2">
        {yn('mixed_use', 'Mixed-use property (more than 20% for business)')}
        {yn('manufactured_home', 'Manufactured home')}
        {yn('fha_secondary_residence', 'FHA secondary residence')}
      </div>
    </div>
  );
}

function OtherMortgagesEditor({ list, onChange }) {
  const add = () => onChange([...list, { lien_type: 'first' }]);
  const upd = (i, patch) => onChange(list.map((x, idx) => idx === i ? { ...x, ...patch } : x));
  const rem = (i) => onChange(list.filter((_, idx) => idx !== i));
  return (
    <div>
      <div className="flex justify-between items-center mb-3">
        <div className="text-xs text-slate-500">{list.length} other new mortgage(s) on this property</div>
        <button onClick={add} disabled={list.length >= 10} className="text-xs font-semibold px-2.5 py-1 rounded-lg border border-slate-200 inline-flex items-center gap-1 disabled:opacity-40" data-testid="add-other-mortgage"><Plus size={11} /> Add mortgage</button>
      </div>
      <div className="space-y-2">
        {list.map((m, i) => (
          <div key={i} className="rounded-lg border border-slate-100 p-3 grid grid-cols-2 md:grid-cols-5 gap-2 relative" data-testid={`other-mortgage-${i}`}>
            <F label="Creditor" value={m.creditor_name} onChange={(v) => upd(i, { creditor_name: v })} testId={`other-mortgage-${i}-creditor`} />
            <SF label="Lien type" value={m.lien_type} onChange={(v) => upd(i, { lien_type: v })} options={[['first', 'First lien'], ['subordinate', 'Subordinate lien']]} />
            <F label="Monthly payment" kind="money" value={m.monthly_payment} onChange={(v) => upd(i, { monthly_payment: toNum(v) })} />
            <F label="Loan amount" kind="money" value={m.loan_amount} onChange={(v) => upd(i, { loan_amount: toNum(v) })} />
            <F label="Credit limit (if a HELOC)" kind="money" value={m.credit_limit} onChange={(v) => upd(i, { credit_limit: toNum(v) })} />
            <button onClick={() => rem(i)} className="absolute top-2 right-2 text-slate-400 hover:text-red-500" aria-label="Remove mortgage"><Trash2 size={13} /></button>
          </div>
        ))}
        {list.length === 0 && <div className="text-xs text-slate-400 italic">None.</div>}
      </div>
    </div>
  );
}

function RentalIncomeEditor({ rental, onChange }) {
  const u = (k, v) => onChange({ ...rental, [k]: toNum(v) });
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
      <F label="Expected monthly rental income" kind="money" value={rental.expected_monthly_rental_income} onChange={(v) => u('expected_monthly_rental_income', v)} testId="rental-expected" />
      <F label="Expected net monthly rental income (investment property only)" kind="money" value={rental.expected_net_monthly_rental_income} onChange={(v) => u('expected_net_monthly_rental_income', v)} testId="rental-expected-net" />
    </div>
  );
}

// =========================================================================
// 6 - Acknowledgments. Read-only: the wording is the lender's legal text, and the
// dates are written only by a completed signing (see UrlaSigningPanel).
// =========================================================================
function AcknowledgementsNote({ urla }) {
  const rows = [['Borrower', urla.borrower_signed_at || null], ['Co-Borrower', urla.coborrower ? (urla.coborrower_signed_at || null) : undefined]];
  return (
    <div className="space-y-2 text-sm text-slate-600" data-testid="urla-acknowledgements">
      <p>The borrower acknowledgments and agreements are signed with the 1003, by DocuSign or on paper. They are not answered in this form.</p>
      <ul className="text-xs space-y-1">
        {rows.filter(([, at]) => at !== undefined).map(([l, at]) => (
          <li key={l}>{l}: {at ? `acknowledged ${formatDate(at)}` : 'not yet signed'}</li>
        ))}
      </ul>
    </div>
  );
}

// =========================================================================
// 9 - Loan originator
// =========================================================================
function OriginatorEditor({ loanId, originator, onChange }) {
  const [busy, setBusy] = useState(false);
  const u = (k, v) => onChange({ ...originator, [k]: v });
  const addr = originator.organization_address || {};
  const fillDefaults = async () => {
    setBusy(true);
    try {
      const d = await losApi.urlaOriginatorDefaults(loanId);
      // Only blanks are filled: what was typed stays.
      const merged = { ...originator };
      Object.entries(d).forEach(([k, v]) => { if (v && !merged[k]) merged[k] = v; });
      onChange(merged);
    } catch (e) { toast({ title: 'Failed', description: errorMessage(e) }); }
    finally { setBusy(false); }
  };
  return (
    <div className="space-y-3">
      <button onClick={fillDefaults} disabled={busy} className="text-xs font-semibold px-2.5 py-1 rounded-lg border border-slate-200 inline-flex items-center gap-1 disabled:opacity-40" data-testid="originator-defaults">
        {busy && <Loader2 size={11} className="animate-spin" />} Fill from loan officer and company
      </button>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <F label="Loan originator name" value={originator.name} onChange={(v) => u('name', v)} testId="originator-name" />
        <F label="Originator NMLS ID" value={originator.nmls_id} onChange={(v) => u('nmls_id', v)} testId="originator-nmls" />
        <F label="State license ID" value={originator.state_license_id} onChange={(v) => u('state_license_id', v)} />
        <F label="Email" value={originator.email} onChange={(v) => u('email', v)} />
        <F label="Phone" kind="phone" value={originator.phone} onChange={(v) => u('phone', v)} />
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <F label="Loan originator organization" value={originator.organization_name} onChange={(v) => u('organization_name', v)} testId="originator-org" />
        <F label="Organization NMLS ID" value={originator.organization_nmls_id} onChange={(v) => u('organization_nmls_id', v)} />
        <F label="Organization state license ID" value={originator.organization_state_license_id} onChange={(v) => u('organization_state_license_id', v)} />
      </div>
      <div>
        <div className="text-xs uppercase tracking-widest text-slate-500 mb-2">Organization address</div>
        <AddressFields addr={addr} onChange={(a) => u('organization_address', a)} prefix="originator-address" />
      </div>
    </div>
  );
}

// =========================================================================
// Declarations — URLA section 5
// =========================================================================
const DECL_QUESTIONS = [
  ['will_occupy_as_primary',        'Will you occupy the property as your primary residence?'],
  ['had_ownership_last_3yr',        'Have you had an ownership interest in another property in the last 3 years?'],
  ['family_relationship_seller',    'Is there a family or business affiliation with the seller?'],
  ['borrowing_money_for_transaction','Are you borrowing money to fund this transaction?'],
  ['applying_other_mortgage',       'Have you applied for a mortgage on another property?'],
  ['applying_new_credit',           'Have you applied for any new credit not disclosed?'],
  ['has_undisclosed_lien_on_property','Will there be a lien on the property not disclosed on the LE?'],
  ['is_co_signer_on_debt',          'Are you a co-signer / guarantor on any debt or loan?'],
  ['outstanding_judgments',         'Are there any outstanding judgments against you?'],
  ['delinquent_federal_debt',       'Are you currently delinquent on any federal debt?'],
  ['party_to_lawsuit',              'Are you a party to a lawsuit that could result in a judgment against you?'],
  ['conveyed_title_lieu_foreclosure_last_7yr','Have you conveyed title in lieu of foreclosure in past 7 years?'],
  ['short_sale_last_7yr',           'Have you completed a short sale in the past 7 years?'],
  ['foreclosure_last_7yr',          'Have you had a property foreclosed on in the past 7 years?'],
  ['bankruptcy_last_7yr',           'Have you declared bankruptcy in the past 7 years?'],
];
const BANKRUPTCY_CHAPTERS = [['chapter_7', 'Chapter 7'], ['chapter_11', 'Chapter 11'], ['chapter_12', 'Chapter 12'], ['chapter_13', 'Chapter 13']];
export function DeclarationsEditor({ decl, onChange }) {
  const setV = (k, v) => onChange({ ...decl, [k]: v });
  return (
    <div className="space-y-2">
      {DECL_QUESTIONS.map(([key, q]) => (
        <div key={key} className="flex items-center justify-between gap-3 p-2.5 rounded-lg hover:bg-slate-50">
          <div className="text-sm text-slate-700 flex-1">{q}</div>
          <div className="flex gap-1">
            <YesNoBtn active={decl[key] === true} onClick={() => setV(key, true)} label="Yes" testId={`decl-${key}-yes`} />
            <YesNoBtn active={decl[key] === false} onClick={() => setV(key, false)} label="No" testId={`decl-${key}-no`} />
          </div>
        </div>
      ))}
      {decl.had_ownership_last_3yr && (
        <div className="pl-4 mt-1 grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="decl-prior-ownership">
          <SF label="5a · Type of property you owned" value={decl.prior_property_type} onChange={(v) => setV('prior_property_type', v)} testId="decl-prior-property-type"
            options={[['primary_residence', 'Primary residence'], ['fha_secondary_residence', 'FHA secondary residence'], ['second_home', 'Second home'], ['investment', 'Investment property']]} />
          <SF label="5a · How you held title" value={decl.prior_title_held} onChange={(v) => setV('prior_title_held', v)} testId="decl-prior-title-held"
            options={[['solely', 'By yourself'], ['with_spouse', 'Jointly with your spouse'], ['with_another', 'Jointly with another person']]} />
        </div>
      )}
      {decl.bankruptcy_last_7yr && (
        <div className="pl-4 mt-1" data-testid="decl-bankruptcy-types">
          <div className="text-xs text-slate-500 mb-1">Bankruptcy chapter(s)</div>
          <div className="flex flex-wrap gap-2">
            {BANKRUPTCY_CHAPTERS.map(([k, l]) => {
              const types = decl.bankruptcy_types || [];
              return <ChipBtn key={k} active={types.includes(k)} testId={`decl-bankruptcy-${k}`} label={l}
                onClick={() => setV('bankruptcy_types', types.includes(k) ? types.filter((t) => t !== k) : [...types, k])} />;
            })}
          </div>
        </div>
      )}
      {decl.borrowing_money_for_transaction && (
        <div className="pl-4 mt-1">
          <F label="Amount borrowed for transaction" kind="money" value={decl.borrowed_money_amount} onChange={(v) => setV('borrowed_money_amount', Number(v))} />
        </div>
      )}
    </div>
  );
}

function YesNoBtn({ active, onClick, label, testId }) {
  return <button onClick={onClick} data-testid={testId} className={`text-xs font-bold px-3 py-1.5 rounded-lg transition ${active ? 'text-white' : 'text-slate-500 hover:bg-slate-100 border border-slate-200'}`} style={active ? { background: label === 'Yes' ? '#6BA82E' : '#070B5F' } : {}}>
    {label}
  </button>;
}

// =========================================================================
// Demographics — URLA section 7 (HMDA)
// =========================================================================
const ETHNICITY = ['Hispanic or Latino', 'Not Hispanic or Latino'];
const ETHNICITY_DETAIL = ['Mexican', 'Puerto Rican', 'Cuban', 'Other Hispanic or Latino'];
const RACE = ['American Indian or Alaska Native', 'Asian', 'Black or African American', 'Native Hawaiian or Other Pacific Islander', 'White'];
const ASIAN_DETAIL = ['Asian Indian', 'Chinese', 'Filipino', 'Japanese', 'Korean', 'Vietnamese', 'Other Asian'];
const PACIFIC_DETAIL = ['Native Hawaiian', 'Guamanian or Chamorro', 'Samoan', 'Other Pacific Islander'];
const ASIAN = 'Asian';
const PACIFIC = 'Native Hawaiian or Other Pacific Islander';
const HISPANIC = 'Hispanic or Latino';
const AMERICAN_INDIAN = 'American Indian or Alaska Native';

export function DemographicsEditor({ demo, onChange, label }) {
  const setV = (k, v) => onChange({ ...demo, [k]: v });
  const setMany = (patch) => onChange({ ...demo, ...patch });
  const has = (k, val) => (demo[k] || []).includes(val);
  // A group's subcategories and write-in belong to it: turning the group off clears them.
  const SUBS = {
    [HISPANIC]: { detail: ['ethnicity_detail', ETHNICITY_DETAIL], other: ['ethnicity_other'] },
    [ASIAN]: { detail: ['race_detail', ASIAN_DETAIL], other: ['race_asian_other'] },
    [PACIFIC]: { detail: ['race_detail', PACIFIC_DETAIL], other: ['race_pacific_other'] },
    [AMERICAN_INDIAN]: { detail: null, other: ['race_tribe'] },
  };
  const clearGroup = (group) => {
    const { detail, other } = SUBS[group] || {};
    const out = {};
    if (detail) out[detail[0]] = (demo[detail[0]] || []).filter((x) => !detail[1].includes(x));
    (other || []).forEach((k) => { out[k] = null; });
    return out;
  };
  // Picking an answer drops "prefer not to answer" for that question, and the other way round.
  const toggleGroup = (key, notKey, val) => {
    const on = has(key, val);
    setMany({
      [key]: on ? (demo[key] || []).filter((x) => x !== val) : [...(demo[key] || []), val],
      [notKey]: false,
      ...(on ? clearGroup(val) : {}),
    });
  };
  const toggleDetail = (key, parentKey, parent, val) => {
    const on = has(key, val);
    setMany({
      [key]: on ? (demo[key] || []).filter((x) => x !== val) : [...(demo[key] || []), val],
      // A subcategory implies its group.
      ...(!on && !has(parentKey, parent) ? { [parentKey]: [...(demo[parentKey] || []), parent], [`${parentKey}_not_provided`]: false } : {}),
    });
  };
  const notProvided = (key, notKey, extra = {}) => setMany({
    [notKey]: !demo[notKey],
    ...(!demo[notKey] ? { [key]: [], ...extra } : {}),
  });
  // Plain functions, not components: a component defined here would remount on every
  // keystroke and drop the focus from the write-in fields.
  const sub = (items, field, parentKey, parent, testPrefix) => (
    <div className="flex flex-wrap gap-2 mt-2 ml-4" data-testid={`${testPrefix}-sub`}>
      {items.map((v) => <ChipBtn key={v} active={has(field, v)} onClick={() => toggleDetail(field, parentKey, parent, v)} label={v} testId={`${testPrefix}-${v}`} />)}
    </div>
  );
  const write = (field, placeholder, testId) => (
    <Input className="mt-2 ml-4 max-w-sm h-8 text-sm" maxLength={100} placeholder={placeholder} data-testid={testId}
      value={demo[field] || ''} onChange={(e) => setV(field, e.target.value || null)} />
  );
  const sexOn = (k) => demo.sex === k;
  const sexNot = demo.sex === 'not_provided' || !!demo.sex_not_provided;
  const obs = (k, text) => (
    <div className="flex items-center justify-between gap-3 text-sm text-slate-600">
      <span>{text}</span>
      <span className="flex gap-2">
        <YesNoBtn active={demo[k] === true} onClick={() => setV(k, demo[k] === true ? null : true)} label="Yes" testId={`demo-${k}-yes`} />
        <YesNoBtn active={demo[k] === false} onClick={() => setV(k, demo[k] === false ? null : false)} label="No" testId={`demo-${k}-no`} />
      </span>
    </div>
  );
  return (
    <div>
      <div className="text-xs uppercase tracking-widest text-slate-500 mb-2">{label} — Government Monitoring (voluntary)</div>
      <div className="space-y-3">
        <div>
          <div className="text-sm font-semibold mb-1" style={{ color: '#070B5F' }}>Ethnicity</div>
          <div className="flex flex-wrap gap-2">
            {ETHNICITY.map(e => <ChipBtn key={e} active={has('ethnicity', e)} onClick={() => toggleGroup('ethnicity', 'ethnicity_not_provided', e)} label={e} />)}
            <ChipBtn active={!!demo.ethnicity_not_provided} onClick={() => notProvided('ethnicity', 'ethnicity_not_provided', { ethnicity_detail: [], ethnicity_other: null })} label="Prefer not to answer" testId="demo-ethnicity-not-provided" />
          </div>
          {has('ethnicity', HISPANIC) && sub(ETHNICITY_DETAIL, 'ethnicity_detail', 'ethnicity', HISPANIC, 'demo-ethnicity')}
          {has('ethnicity_detail', 'Other Hispanic or Latino') && write('ethnicity_other', 'Origin, for example Salvadoran', 'demo-ethnicity-other')}
        </div>
        <div>
          <div className="text-sm font-semibold mb-1" style={{ color: '#070B5F' }}>Race</div>
          <div className="flex flex-wrap gap-2">
            {RACE.map(r => <ChipBtn key={r} active={has('race', r)} onClick={() => toggleGroup('race', 'race_not_provided', r)} label={r} />)}
            <ChipBtn active={!!demo.race_not_provided} onClick={() => notProvided('race', 'race_not_provided', { race_detail: [], race_tribe: null, race_asian_other: null, race_pacific_other: null })} label="Prefer not to answer" testId="demo-race-not-provided" />
          </div>
          {has('race', AMERICAN_INDIAN) && write('race_tribe', 'Name of enrolled or principal tribe', 'demo-race-tribe')}
          {has('race', ASIAN) && sub(ASIAN_DETAIL, 'race_detail', 'race', ASIAN, 'demo-asian')}
          {has('race_detail', 'Other Asian') && write('race_asian_other', 'Race, for example Hmong', 'demo-asian-other')}
          {has('race', PACIFIC) && sub(PACIFIC_DETAIL, 'race_detail', 'race', PACIFIC, 'demo-pacific')}
          {has('race_detail', 'Other Pacific Islander') && write('race_pacific_other', 'Race, for example Tongan', 'demo-pacific-other')}
        </div>
        <div>
          <div className="text-sm font-semibold mb-1" style={{ color: '#070B5F' }}>Sex</div>
          <div className="flex gap-2">
            {[['male','Male'],['female','Female']].map(([k,l]) =>
              <ChipBtn key={k} active={sexOn(k)} onClick={() => setMany({ sex: sexOn(k) ? null : k, sex_not_provided: false })} label={l} />)}
            <ChipBtn active={sexNot} onClick={() => setMany(sexNot ? { sex: demo.sex === 'not_provided' ? null : demo.sex, sex_not_provided: false } : { sex: null, sex_not_provided: true })} label="Prefer not to answer" testId="demo-sex-not-provided" />
          </div>
        </div>
        <div className="space-y-2 pt-2 border-t border-slate-100" data-testid="demo-lender-block">
          <div className="text-xs uppercase tracking-widest text-slate-500">To be completed by the lender</div>
          {obs('ethnicity_by_observation', 'Ethnicity collected on the basis of visual observation or surname?')}
          {obs('race_by_observation', 'Race collected on the basis of visual observation or surname?')}
          {obs('sex_by_observation', 'Sex collected on the basis of visual observation or surname?')}
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 pt-2 border-t border-slate-100">
          <SF label="Method of collection" value={demo.method_of_collection} onChange={(v) => setV('method_of_collection', v)}
            options={[['face_to_face','Face-to-face'],['telephone','Telephone'],['internet','Internet'],['mail','Mail']]} />
        </div>
      </div>
    </div>
  );
}

function ChipBtn({ active, onClick, label, testId }) {
  return <button onClick={onClick} data-testid={testId} className={`text-xs font-semibold px-3 py-1.5 rounded-full border transition ${active ? 'text-white' : 'text-slate-600 hover:bg-slate-50'}`} style={active ? { background: '#6BA82E', borderColor: '#6BA82E' } : { borderColor: '#e2e8f0' }}>
    {label}
  </button>;
}

// =========================================================================
// Field primitives
// =========================================================================
function F({ label, value, onChange, type = 'text', step, placeholder, kind, integer, testId }) {
  let field;
  if (kind === 'phone') {
    field = <PhoneInput value={value} onChange={onChange} placeholder={placeholder} className="mt-1" data-testid={testId} />;
  } else if (kind === 'money') {
    field = <MoneyInput value={value} onChange={onChange} placeholder={placeholder} className="mt-1" data-testid={testId} />;
  } else if (kind === 'number' || type === 'number') {
    field = <NumberInput integer={!!integer} value={value} onChange={onChange} placeholder={placeholder} className="mt-1" data-testid={testId} />;
  } else {
    field = <Input type={type} step={step} placeholder={placeholder} value={value ?? ''} onChange={(e) => onChange(e.target.value)} className="mt-1" data-testid={testId} />;
  }
  return (
    <div>
      <Label className="text-xs text-slate-500">{label}</Label>
      {field}
    </div>
  );
}
function SF({ label, value, onChange, options, testId }) {
  return (
    <div>
      <Label className="text-xs text-slate-500">{label}</Label>
      <Select value={value ?? ''} onValueChange={onChange}>
        <SelectTrigger className="mt-1" data-testid={testId}><SelectValue /></SelectTrigger>
        <SelectContent>{options.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
      </Select>
    </div>
  );
}
